import { useCallback, useEffect, useRef } from "react";
import { sendExtensionMessage } from "../lib/extensionMessaging";
import { announceOrgPolicyChanged, setOrgPolicyNotifier } from "../lib/orgPolicyBroadcast";

/**
 * The console's org-policy notifier: what runs after a write to policy the extension reads. Returned
 * for `useOrgSettings`, and registered for the hooks that call `notifyOrgPolicyChanged()` (ENG-2990,
 * lib/orgPolicyBroadcast.ts, for why it is registered rather than passed down). Built here rather than
 * inline in App.tsx so that what it does can be tested.
 *
 * Everything that holds a copy of the policy is told:
 *
 * - **Each detected Haven extension** (ENG-2671), which re-reads its plan now rather than at its
 *   five-minute refresh. Enforcement mode is a security control, and an admin switching their org to
 *   Active Enforce used to watch their own browser keep running Monitor-Only. Best-effort: a browser
 *   with no extension, or one that doesn't answer, must not fail the save; the extension's refresh
 *   alarm is what guarantees delivery for everyone else.
 * - **The hoisted Risk Report** (ENG-3366), dropped so the next visit re-fetches it rather than
 *   reusing one whose Policy column predates the change.
 * - **The console's own `/subscription-status`** (ENG-3416), re-read. It is the plan the extension
 *   reads, resolved for the viewer, and the console reads the viewer's feature flags from it: the
 *   Email Scanning entry and Settings' Gmail card follow `gmailLinkScanning`. Held from sign-in, it
 *   went on saying a flag was off after the admin had turned it on.
 * - **Overview's dashboard** (ENG-3527), whose cache key moves so the next fetch goes to the server.
 *   It carries the enforcement mode, and went on saying Monitor-Only after a switch to Active
 *   Enforcement until a reload.
 * - **Anything listening on `onOrgPolicyChanged`** (ENG-3527): the console's copies App.tsx cannot
 *   reach by name. Today that is Overview's "Needs your attention" (useAttention, held by
 *   DashboardLayout), whose "Deny" on an extension just denied would otherwise stay clickable.
 *
 *   Not the deny list: since ENG-3368 the Extensions page reads the ORGANIZATION's `extensionDenyList`
 *   and the groups that set their own from `/business/feature-flags` (`denyListReachOf`), which
 *   `useOrgSettings` updates on its own writes. The viewer's value here is resolved through their own
 *   groups, so it is the wrong answer to "is the deny list on for the organization".
 */
export function useOrgPolicyNotifier({ getAllExtensionIds, invalidateRiskReport, invalidateOverview, refreshSubscriptionStatus }: {
  getAllExtensionIds: () => string[];
  invalidateRiskReport: () => void;
  invalidateOverview: () => void;
  refreshSubscriptionStatus: () => unknown;
}): () => void {
  // `useSubscription` builds its fetch afresh every render. Held in a ref so the notifier keeps one
  // identity for as long as the extension ids do (ENG-2722), and still calls the latest fetch.
  const refreshRef = useRef(refreshSubscriptionStatus);
  refreshRef.current = refreshSubscriptionStatus;

  const notify = useCallback(() => {
    invalidateRiskReport();
    invalidateOverview();
    announceOrgPolicyChanged();
    void refreshRef.current();
    getAllExtensionIds().forEach((id) => {
      sendExtensionMessage(id, { action: "org-policy-updated" }).catch(() => {});
    });
  }, [getAllExtensionIds, invalidateRiskReport, invalidateOverview]);

  useEffect(() => {
    setOrgPolicyNotifier(notify);
    return () => setOrgPolicyNotifier(null);
  }, [notify]);

  return notify;
}
